import { type ReactNode, useEffect, useState } from "react"
import { Button } from "../ui/button/Button"
import { Switch } from "../ui/input/switch/Switch"
import { Modal } from "../ui/overlays/modal/Modal"
import { setConsentDecision } from "./consent"
import { PRIVACY_POLICY_URL } from "./constants"
import { useConsent } from "./useConsent"

type ConsentCategoryProps = {
  title: string
  description: string
  control?: ReactNode
}

const ConsentCategory = ({
  title,
  description,
  control,
}: ConsentCategoryProps) => (
  <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-bg-inset">
    <div className="flex flex-col gap-1">
      <span className="text-sm text-fg-default">{title}</span>
      <span className="text-sm text-fg-secondary text-pretty">
        {description}
      </span>
    </div>
    {control}
  </div>
)

export type ConsentSettingsDialogProps = {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  privacyPolicyUrl?: string
}

export function ConsentSettingsDialog({
  isOpen,
  onOpenChange,
  privacyPolicyUrl = PRIVACY_POLICY_URL,
}: ConsentSettingsDialogProps) {
  const { isPerformanceAllowed } = useConsent()
  const [isPerformanceOn, setIsPerformanceOn] = useState(isPerformanceAllowed)

  // Reopening the dialog should show what is currently stored, not the last edit.
  useEffect(() => {
    if (isOpen) {
      setIsPerformanceOn(isPerformanceAllowed)
    }
  }, [isOpen, isPerformanceAllowed])

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="large"
      aria-label="Manage your preferences"
    >
      <Modal.Header title="Manage your preferences" />
      <Modal.Body>
        <p className="text-sm text-fg-secondary text-pretty">
          Some cookies are needed for the site to work. Others help us
          understand how Token Terminal is used, so we can keep improving it. By
          continuing to browse, you agree to our{" "}
          <a href={privacyPolicyUrl} className="underline">
            privacy policy
          </a>
          .
        </p>
        <ConsentCategory
          title="Necessary cookies"
          description="Required for signing in and remembering your choices. The site can't work without them, and they can only be turned off in your browser settings."
        />
        <ConsentCategory
          title="Performance"
          description="These help us understand how the site performs and where to improve it."
          control={
            <Switch
              size="small"
              aria-label="Performance"
              isSelected={isPerformanceOn}
              onChange={setIsPerformanceOn}
              className="shrink-0 mt-1"
            />
          }
        />
      </Modal.Body>
      <Modal.Footer className="justify-end">
        <Button
          variant="solid"
          onPress={() => {
            setConsentDecision(
              isPerformanceOn ? "accepted" : "rejected",
              "banner"
            )
            onOpenChange(false)
          }}
        >
          Save preferences
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
